import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import BasicLayout from "@container/layout/basic";
import { getInviteTeamDetail } from "@service/auth";
import { CompanyJoinTeam } from "@type/company";
import { ArrowRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@util/useQuery";
import { Checkbox } from "@component/checkbox";

export default function EmployerVerifyTeam() {
    const queryParams = useQuery()
    const navigate = useNavigate();
    const token = queryParams.get("token") || "";
    const payloadToken = token ? JSON.parse(atob(token)) : null;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [company, setCompany] = useState<CompanyJoinTeam | undefined>(undefined);
    const [termsAccepted, setTermsAccepted] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        getInviteTeamDetail(
            payloadToken["email"] ?? "",
        ).then((resp) => {
            if (resp.status === 200) {
                // An Oura merchant's colleague signs up on Oura's own page, with the same
                // token: invites already emailed still point here (#4859).
                if (resp.data?.data?.is_oura_client && token) {
                    navigate(`/oura/join?token=${encodeURIComponent(token)}`, { replace: true });
                    return;
                }
                setCompany(resp.data.data);
            }
        }).finally(() => {
            setIsLoading(false)
            sessionStorage.setItem('__INVITE_MEMBER_EMAIL__', payloadToken["email"] ?? "")
        })
    }, [queryParams.get])

    const handleGoToRegistration = () => {
        navigate('/employer-signup')
    }

    return (
        <BasicLayout disableMinHeight showFooter={false}>
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 h-[calc(100vh-57px)]">
                <Card className="w-full max-w-xl">
                    <CardContent
                        className="p-6 space-y-6"
                        style={{
                            paddingTop: "32px",
                            paddingRight: "32px",
                            paddingBottom: "32px",
                            paddingLeft: "32px",
                        }}
                    >
                        {/* Header */}
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                You’ve been invited to join{" "}
                                <span className="font-bold">{company?.company_name}</span>'s workspace on ourteam
                            </h2>
                            <p className="text-sm text-gray-600 font-medium">
                                Please register as{" "}
                                <span className="font-bold">{payloadToken["email"] || "-"}</span> to accept this
                                invitation.
                            </p>
                        </div>

                        {/* Company Info */}
                        <div>
                            <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/30">
                                {/* Decorative background elements */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/40 rounded-full blur-3xl" />

                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />

                                <div className="relative p-6">
                                    <div className="flex flex-col items-center space-y-4 text-center">
                                        {/* Company Logo with enhanced styling */}
                                        <div className="relative flex-shrink-0 w-16 h-16 rounded-full overflow-hidden flex justify-center items-center bg-white ring-1 ring-blue-200 dark:ring-offset-gray-900">
                                            <img
                                                src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${company?.company_logo}`}
                                                alt={`${company?.company_name} logo`}
                                                onError={(e) => {
                                                    e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                                                }}
                                                className="relative w-16 object-cover "
                                            />
                                        </div>

                                        {/* Company Details */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-gray-900 mb-1">
                                                {company?.company_name}
                                            </h3>
                                            <p className="text-sm text-gray-600 font-medium mb-2">
                                                {company?.num_of_member} member{company?.num_of_member !== 1 ? "s" : ""}
                                            </p>
                                            <div className="flex items-center justify-center">
                                                <p className="text-sm text-gray-600">
                                                    Invited by{" "}
                                                    <span className="font-semibold text-gray-900">
                                                        {company?.invited_by}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 w-full">
                            <Checkbox
                                id="terms"
                                checked={termsAccepted}
                                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                                className="mt-0.5"
                            />
                            <label
                                htmlFor="terms"
                                className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer leading-relaxed"
                            >
                                By proceeding, you agree to the{" "}
                                <a
                                    href="https://ourteam.ai/terms-of-service"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline text-blue-600 dark:text-blue-400 hover:text-black dark:hover:text-white"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Terms of Service
                                </a>{" "}
                                at ourteam.
                            </label>
                        </div>

                        {/* Accept Invitation Button */}
                        <div className="text-center">
                            <Button
                                onClick={handleGoToRegistration}
                                disabled={isLoading || !company || !termsAccepted}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                                style={{ paddingTop: "16px", paddingBottom: "16px" }}
                            >
                                {isLoading ? "Accepting..." : "Register to Accept Invitation"}
                                {!isLoading && <ArrowRightIcon className="ml-2 h-4 w-4" />}
                            </Button>

                            <div className="mt-6 text-center text-sm">
                                Already have an account?{" "}
                                <span className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                    <Link to="/login" className="underline">
                                        Login here.
                                    </Link>
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </BasicLayout>
    );
}
